'use client'

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { usePathname } from '@/i18n/navigation'
import ProductDetailModal from './modal'
import { IProduct } from '../../_types'

type ProductModalContextValue = {
  openProduct: (data: IProduct) => void
  closeProduct: () => void
}

const ProductModalContext =
  createContext<ProductModalContextValue | null>(null)

export function ProductModalProvider({
  children,
}: {
  children: React.ReactNode
}) {
  const [data, setData] = useState<IProduct | undefined>(undefined)
  const pathname = usePathname()

  const openProduct = useCallback((nextData: IProduct) => {
    setData(nextData)
  }, [])

  const closeProduct = useCallback(() => {
    setData(undefined)
  }, [])

  useEffect(() => {
    closeProduct()
  }, [pathname, closeProduct])

  const value = useMemo(
    () => ({ openProduct, closeProduct }),
    [openProduct, closeProduct]
  )

  return (
    <ProductModalContext.Provider value={value}>
      {children}
      {data ? (
        <ProductDetailModal data={data} onClose={closeProduct} />
      ) : null}
    </ProductModalContext.Provider>
  )
}

export function useProductModal() {
  const ctx = useContext(ProductModalContext)
  if (!ctx) {
    throw new Error(
      'useProductModal must be used within ProductModalProvider'
    )
  }
  return ctx
}
